import Drawer from '@material-ui/core/Drawer'
import IconButton from '@material-ui/core/IconButton'
import CloseIcon from '@material-ui/icons/Close'
import FilterListIcon from '@material-ui/icons/FilterList'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import minMax from 'dayjs/plugin/minMax'
import { isEmpty } from 'lodash'
import React, { useState } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { FullScreen, useFullScreenHandle } from 'react-full-screen'

import { updateAttendance } from 'api'
import { getAttendanceData } from 'api/attendance'
import { getBatchData } from 'api/batch'
import { AutoComplete } from 'app/components/generic/FormFields'
import History from 'app/components/generic/history/history'
import { Loading } from 'app/components/generic/LottieComponents'
import Pill from 'app/components/generic/Pill'
import constants from 'app/constants'
import { confirm, eventAlert } from 'app/helpers'
import { useIsMobile } from 'app/hooks/useIsMobile'
import { colors } from 'app/theme/colors'
import { space } from 'app/theme/tokens'

import SliderCard from './components/SliderCard'
import {
  dataColumns,
  emptyStudent,
  filterPillList,
  studentType,
  subjectsList
} from './config'
import {
  fetchParent,
  fetchSchedules,
  fetchStudent,
  getCentre,
  updateAttendanceData
} from './helpers'
import * as styles from './styles'

import BookDemoClass from '../Class/Inventory/components/ModalsAndActions/components/BookDemoClass'
import { useBatchSelection } from '../Class/Inventory/components/ModalsAndActions/hooks/useBatchSelection'
import { BookingTypes } from '../Class/Inventory/constants'

dayjs.extend(customParseFormat)
dayjs.extend(minMax)

const Attendance = () => {
  const [formData, setFormData] = React.useState({
    centre: undefined,
    student: undefined,
    parent: undefined
  })
  const handle = useFullScreenHandle()
  const [isLoading, setIsLoading] = useState(true)
  const [componentDisabled, setComponentDisabled] = useState(false)
  const [schedule, setSchedule] = React.useState([])
  const [filterAttendance, setFilterAttendance] = React.useState([])
  const [sliderAttendance, setSliderAttendance] = React.useState({})
  const [startDate, setStartDate] = useState(new Date())
  const [attendanceType, setAttendanceType] = useState('present')
  const centreDefaultValue = localStorage.getItem('CENTRE_SELECT')
    ? JSON.parse(localStorage.getItem('CENTRE_SELECT'))
    : null
  const [isBookDemoClassModalOpen, setIsBookDemoClassModalOpen] =
    React.useState(false)
  const [bookingType, setBookingType] = React.useState(null)
  const [cartItems, setCartItems] = React.useState([])
  const { deselectBatch } = useBatchSelection()
  const [selectedBatchType, setSelectedBatchType] = React.useState()
  let timer
  const [subjectFilter, setSubjectFilter] = useState(null)
  const [pillFilter, setPillFilter] = useState('enrolments') // 'all', 'enrolments', 'cancelledOrZero'

  const isMobile = useIsMobile()
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)

  // Count how many filters are non-default — surfaces as a badge on the
  // mobile "Filters" button so users can see at a glance that something is
  // narrowing the schedule list.
  const activeFilterCount =
    (formData.centre ? 1 : 0) +
    (formData.student ? 1 : 0) +
    (formData.parent ? 1 : 0) +
    (subjectFilter ? 1 : 0) +
    (pillFilter !== 'enrolments' ? 1 : 0)

  // Helper to get unique subjects from schedule
  const uniqueSubjects = React.useMemo(() => {
    const allSubjects = schedule.flatMap(
      (s) => s?.batch?.course?.subjects?.map((subj) => subj.name) || []
    )
    return [...new Set(allSubjects)]
  }, [schedule])

  // Filtered schedule based on subject and pill
  const filteredSchedule = React.useMemo(() => {
    let filtered = schedule
    if (subjectFilter) {
      filtered = filtered.filter((s) =>
        s?.batch?.course?.subjects?.some((subj) => subj.name === subjectFilter)
      )
    }
    if (pillFilter === 'enrolments') {
      filtered = filtered.filter((s) => !s.cancelled && s.reserved_count > 0)
    } else if (pillFilter === 'cancelledOrZero') {
      filtered = filtered.filter((s) => s.cancelled || s.reserved_count === 0)
    }
    return filtered
  }, [schedule, subjectFilter, pillFilter])

  const handleChange = (name, value) => {
    const data = {
      ...formData
    }

    switch (name) {
      case 'centre':
        data.centre = value
        setFormData(data)
        localStorage.setItem('CENTRE_SELECT', JSON.stringify(value))
        break
      case 'student':
        if (value) data.parent = undefined
        data.student = value
        setFormData(data)
        localStorage.setItem('STUDENT', JSON.stringify(value))
        break
      case 'parent':
        if (value) data.student = undefined
        data.parent = value
        setFormData(data)
        localStorage.setItem('PARENT', JSON.stringify(value))
        break
    }
  }

  React.useEffect(() => {
    // get centre name from url query
    const queries = new URLSearchParams(window.location.search)

    const centreName = queries.get('centre_name')
    if (centreName) {
      async function centres() {
        const response = await getCentre(centreName)
        if (response.length) handleChange('centre', response[0])
      }
      centres()
    }
  }, [])

  const fetchValues = async (type, id) => {
    let res
    if (type === 'parent' || type === 'student') {
      res = await fetchSchedules(
        type,
        id,
        setIsLoading,
        formData,
        setSchedule,
        setFilterAttendance,
        timer,
        startDate
      )
    } else {
      res = await fetchSchedules(
        null,
        null,
        setIsLoading,
        formData,
        setSchedule,
        setFilterAttendance,
        timer,
        startDate
      )
    }
    return res
  }

  React.useEffect(() => {
    setIsLoading(true)
    fetchValues(null, null)
  }, [formData.centre])

  React.useEffect(() => {
    if (formData.student?.uuid) {
      setIsLoading(true)
      fetchValues('student', formData.student?.uuid)
    } else fetchValues(null, null)
  }, [formData.student])

  React.useEffect(() => {
    if (formData.parent?.uuid) {
      setIsLoading(true)
      fetchValues('parent', formData.parent?.uuid)
    } else fetchValues(null, null)
  }, [formData.parent])

  React.useEffect(() => {
    if (startDate) {
      if (formData.parent?.uuid) {
        setIsLoading(true)
        fetchValues('parent', formData.parent?.uuid)
      } else if (formData.student?.uuid) {
        setIsLoading(true)
        fetchValues('student', formData.student?.uuid)
      } else fetchValues(null, null)
    }
  }, [startDate])

  const attendanceChange = async (e, type, el) => {
    if (type === 'attended') {
      setComponentDisabled(true)
      const onSave = async () => {
        let attendanceResponse
        try {
          attendanceResponse = await updateAttendance(el)
        } catch (error) {
          eventAlert(error.message, constants.ERROR)
        } finally {
          if (!isEmpty(attendanceResponse)) {
            updateAttendanceData(
              attendanceResponse,
              formData,
              filterAttendance,
              setFilterAttendance,
              schedule,
              setSchedule,
              sliderAttendance,
              setSliderAttendance
            )
          }
        }
      }
      setComponentDisabled(false)

      const onError = async () => {
        setComponentDisabled(false)
      }

      if (!dayjs(startDate).isToday()) {
        confirm(onSave, 'You are marking attendance in the past', onError)
      } else {
        onSave()
      }
    }
  }

  const onHandleChange = (update) => {
    setIsLoading(true)
    setStartDate(update)
    const type = dayjs(update).isAfter(dayjs(), 'day') ? 'future' : 'present'
    setAttendanceType(type)
  }

  const onHandleDemoClassCloseClick = () => {
    setIsBookDemoClassModalOpen(false)
    setBookingType(null)
    deselectBatch({ bulk: true })
  }

  const onBookingSuccess = () => {
    setCartItems([])
  }

  const fetchBatchData = async (key) => {
    let response = await getBatchData({
      queries: {
        uuid: key
      }
    })
    return response
  }

  const onSpotBookingHandler = async (value) => {
    const batchResponse = await fetchBatchData(value.batch.uuid)
    setCartItems(batchResponse?.results)
    setSelectedBatchType(value?.batch?.type)
    setBookingType(BookingTypes.EXPLORATION)
    setIsBookDemoClassModalOpen(true)
  }

  // The 3 entity dropdowns (centre/student/parent) — only Centre is needed
  // for the common "mark attendance for today" flow; Student/Parent are
  // power-user lookups. On desktop they live inline at the top of the page;
  // on mobile they get folded behind a "Filters" sheet.
  const entityFilters = (
    <styles.RightHeader>
      <styles.DropdownContainer>
        <AutoComplete
          name="centre"
          label="Centre *"
          size="small"
          multiple={false}
          fetchValues={getCentre}
          onChange={handleChange}
          initialSelectIndex={2}
          {...(formData?.centre
            ? { defaultValue: formData?.centre }
            : centreDefaultValue?.value
            ? { defaultValue: centreDefaultValue }
            : null)}
        />
      </styles.DropdownContainer>
      <styles.DropdownContainer>
        <AutoComplete
          async={true}
          name="student"
          label="Student *"
          size="small"
          multiple={false}
          fetchValues={fetchStudent}
          onChange={handleChange}
          emptyValue={formData.student ? false : true}
        />
      </styles.DropdownContainer>
      <styles.DropdownContainer>
        <AutoComplete
          async={true}
          name="parent"
          label="Parent *"
          size="small"
          multiple={false}
          fetchValues={fetchParent}
          onChange={handleChange}
          emptyValue={formData.parent ? false : true}
        />
      </styles.DropdownContainer>
    </styles.RightHeader>
  )

  // Subject + pill filters — these slim the schedule list. Common enough that
  // they live inline on desktop but get folded into the same mobile sheet.
  const sessionFilters = (
    <styles.PillContainer>
      <styles.DropdownContainer style={{ minWidth: 200, maxWidth: 300 }}>
        <AutoComplete
          name="subject"
          label="Subject"
          size="small"
          multiple={false}
          mappedOptions={uniqueSubjects.map((s) => ({ name: s, value: s }))}
          onChange={(name, value) => setSubjectFilter(value ? value.name : null)}
          value={
            subjectFilter
              ? { name: subjectFilter, value: subjectFilter }
              : null
          }
          placeholder="Select Subject"
          clearOnBlur
        />
      </styles.DropdownContainer>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {filterPillList.map((item) => (
          <Pill
            showPillIcon={false}
            text={item.displayName}
            pillStyles={styles.pillStyles}
            selectedPillStyles={styles.selectedPillStyles}
            isSelected={item.name === pillFilter}
            onClick={() => setPillFilter(item.name)}
            pointerDisplay={true}
            key={item.name}
          />
        ))}
      </div>
    </styles.PillContainer>
  )

  return (
      <>
        <FullScreen handle={handle}>
          <styles.Page>
            <styles.Header>
              {/* Mobile: just a compact "Filters" button at the top of the
                  header strip. Tap to open the filters sheet from below. */}
              {isMobile ? (
                <button
                  type="button"
                  onClick={() => setMobileFiltersOpen(true)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    background: colors.white,
                    border: `1px solid ${colors.borderDefault}`,
                    borderRadius: 8,
                    padding: '10px 14px',
                    fontSize: 14,
                    fontWeight: 600,
                    color: colors.textPrimary,
                    cursor: 'pointer'
                  }}
                  aria-label="open filters"
                >
                  <FilterListIcon fontSize="small" />
                  Filters
                  {activeFilterCount > 0 && (
                    <span
                      style={{
                        background: colors.ohOrange,
                        color: colors.white,
                        borderRadius: 999,
                        minWidth: 20,
                        height: 20,
                        padding: '0 6px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12
                      }}
                    >
                      {activeFilterCount}
                    </span>
                  )}
                </button>
              ) : (
                entityFilters
              )}
              <styles.Container>
                <DatePicker
                  showYearDropdown
                  showMonthDropdown
                  dateFormat="dd/MM/yyyy"
                  selected={startDate}
                  onChange={onHandleChange}
                  placeholderText={'Date'}
                  className="custom-date-picker-input"
                />
              </styles.Container>
            </styles.Header>
            {/* On desktop the subject + pill row stays inline. On mobile it
                lives inside the Filters sheet instead. */}
            {!isMobile && sessionFilters}
            {isLoading ? (
              <styles.Loader>
                <Loading />
              </styles.Loader>
            ) : formData.student ? (
              <styles.TableContainer>
                <History
                  type={dataColumns[studentType[attendanceType]]?.type}
                  heading={dataColumns[studentType[attendanceType]]?.heading}
                  userInfo={formData.student?.user}
                  tableHeadings={
                    dataColumns[studentType[attendanceType]]?.tableHeadings
                  }
                  accessors={
                    dataColumns[studentType[attendanceType]]?.accessors
                  }
                  data={filterAttendance}
                  readOnly={true}
                  editableDetails={false}
                  addDetails={false}
                  headerData={true}
                  componentChange={attendanceChange}
                  componentDisabled={componentDisabled}
                />
              </styles.TableContainer>
            ) : formData.parent ? (
              <styles.ChildCards>
                <styles.TableContainer>
                  <History
                    type={dataColumns[studentType[attendanceType]]?.type}
                    heading={dataColumns[studentType[attendanceType]]?.heading}
                    userInfo={formData.parent?.students?.user || emptyStudent}
                    tableHeadings={
                      dataColumns[studentType[attendanceType]]?.tableHeadings
                    }
                    accessors={
                      dataColumns[studentType[attendanceType]]?.accessors
                    }
                    data={filterAttendance}
                    readOnly={true}
                    editableDetails={false}
                    addDetails={false}
                    componentChange={attendanceChange}
                    componentDisabled={componentDisabled}
                  />
                </styles.TableContainer>
              </styles.ChildCards>
            ) : !formData.centre ? (
              // No centre picked yet — on mobile the centre dropdown lives
              // behind the Filters sheet, so users would otherwise stare at
              // a blank page wondering what to do. Tell them.
              <styles.Body>
                <div
                  style={{
                    padding: `${space.lg} ${space.md}`,
                    textAlign: 'center',
                    color: colors.textSecondary
                  }}
                >
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 600,
                      marginBottom: space.xs,
                      color: colors.textPrimary
                    }}
                  >
                    Pick a centre to see today's schedule
                  </div>
                  <div style={{ fontSize: 14, marginBottom: space.md }}>
                    {isMobile
                      ? 'Tap “Filters” above to choose a centre.'
                      : 'Choose a centre from the dropdown above.'}
                  </div>
                  {isMobile && (
                    <button
                      type="button"
                      onClick={() => setMobileFiltersOpen(true)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        background: colors.ohOrange,
                        color: colors.white,
                        border: 'none',
                        borderRadius: 8,
                        padding: '10px 16px',
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      <FilterListIcon fontSize="small" />
                      Open filters
                    </button>
                  )}
                </div>
              </styles.Body>
            ) : (
              <styles.Body>
                {(() => {
                  // Group schedules by hour
                  const schedulesByTime = filteredSchedule.reduce(
                    (acc, schedule) => {
                      if (!schedule?.start_time) return acc

                      // Extract hour from start_time (e.g., "17:15" -> "17")
                      const hour = schedule.start_time.split(':')[0]
                      const hourKey = `${hour}:00`

                      if (!acc[hourKey]) {
                        acc[hourKey] = []
                      }
                      acc[hourKey].push(schedule)
                      return acc
                    },
                    {}
                  )

                  // Sort time keys
                  const sortedTimeKeys = Object.keys(schedulesByTime).sort(
                    (a, b) => {
                      const timeA = parseInt(a.split(':')[0])
                      const timeB = parseInt(b.split(':')[0])
                      return timeA - timeB
                    }
                  )

                  return (
                    <styles.TimelineContainer>
                      {sortedTimeKeys.map((timeKey) => (
                        <styles.TimelineRow key={timeKey}>
                          <styles.TimeLabel>
                            {dayjs(`1/1/1 ${timeKey}`).format('h:mm A')}
                          </styles.TimeLabel>
                          <styles.SchedulesContainer>
                            {schedulesByTime[timeKey].map((value, index) => (
                              <SliderCard
                                key={'Slider card ' + value.uuid}
                                value={value}
                                index={index}
                                attendanceChange={attendanceChange}
                                componentDisabled={componentDisabled}
                                fetchValues={fetchValues}
                                attendance={sliderAttendance}
                                setAttendance={setSliderAttendance}
                                onSpotBookingHandler={() =>
                                  onSpotBookingHandler(value)
                                }
                              />
                            ))}
                          </styles.SchedulesContainer>
                        </styles.TimelineRow>
                      ))}
                    </styles.TimelineContainer>
                  )
                })()}
              </styles.Body>
            )}
          </styles.Page>
        </FullScreen>
        <BookDemoClass
          isModalOpen={isBookDemoClassModalOpen}
          bookingType={bookingType}
          cartItems={cartItems}
          onBookingSuccess={onBookingSuccess}
          onHandleDemoClassCloseClick={onHandleDemoClassCloseClick}
          setIsBookDemoClassModalOpen={setIsBookDemoClassModalOpen}
          selectedBatchType={selectedBatchType}
          setCartItems={setCartItems}
          setBookingType={setBookingType}
          isOnSpotBooking={true}
        />
        {/* Mobile filters sheet — opens from the bottom, contains the three
            entity dropdowns plus the subject/pill filters. Tap outside or
            the X to dismiss. Active count is shown on the button that
            opens it. */}
        <Drawer
          anchor="bottom"
          open={isMobile && mobileFiltersOpen}
          onClose={() => setMobileFiltersOpen(false)}
          PaperProps={{
            style: {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              padding: space.md,
              maxHeight: '80vh'
            }
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: space.md
            }}
          >
            <span style={{ fontSize: 18, fontWeight: 600 }}>Filters</span>
            <IconButton
              aria-label="close filters"
              size="small"
              onClick={() => setMobileFiltersOpen(false)}
            >
              <CloseIcon />
            </IconButton>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}>
            {entityFilters}
            {sessionFilters}
          </div>
        </Drawer>
      </>
    )
}

export default Attendance
